/**
 * Structured data (JSON-LD) for SEO
 * Adds Organization and SoftwareApplication schema
 */

import { config } from '@/lib/config';
import { getLogoPathForSEO } from '@/lib/constants/logo-paths';
import { REVIEW_AGGREGATE_RATING, REVIEW_COUNT } from '@/lib/seo/reviews-data';
import {
  MONTHLY_PRICE_SCHEMA,
  MONTHLY_PRICE_DISPLAY,
  PRICE_CURRENCY,
  TRIAL_DAYS,
} from '@/lib/constants/pricing';
import { JSONLDScript } from './jsonld-script';

export function StructuredData() {
  const baseUrl = config.appUrl;

  // Roll the offer validity to the end of next year so it never silently expires.
  const offerValidUntil = `${new Date().getFullYear() + 1}-12-31`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Email Ferret',
    url: baseUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}${getLogoPathForSEO()}`,
      width: 512,
      height: 512,
    },
    description:
      'Email Ferret catches both traditional spam and sophisticated AI-generated cold outreach that traditional filters miss.',
    sameAs: ['https://twitter.com/email_ferret', 'https://www.linkedin.com/company/110188068'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@emailferret.io',
      availableLanguage: 'English',
    },
  };

  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Email Ferret',
    applicationCategory: 'EmailApplication',
    applicationSubCategory: 'Email Filtering Software',
    operatingSystem: 'Web',
    image: [`${baseUrl}/og-image.png`, `${baseUrl}${getLogoPathForSEO()}`],
    brand: {
      '@type': 'Brand',
      name: 'Email Ferret',
    },
    offers: {
      '@type': 'Offer',
      price: MONTHLY_PRICE_SCHEMA,
      priceCurrency: PRICE_CURRENCY,
      priceValidUntil: offerValidUntil,
      availability: 'https://schema.org/InStock',
      description: `${TRIAL_DAYS}-day free trial, then ${MONTHLY_PRICE_DISPLAY}/month per mailbox`,
      // Digital subscription: delivered electronically, so shipping is free
      // and instant. Modeled explicitly to satisfy Merchant listing validation.
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 0,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 0,
            unitCode: 'DAY',
          },
        },
      },
      // No physical goods to return; refunds are handled via subscription support.
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'US',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
      },
    },
    description:
      'Email Ferret automatically detects and labels AI/BDR outreach emails in Gmail using advanced heuristic analysis. Works 24/7 to detect spam, organize your inbox, and learn your preferences.',
    url: baseUrl,
    // Sourced from the same testimonials rendered on /reviews so the on-page
    // reviews and this aggregate can never disagree. Enables star ratings in SERPs.
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: REVIEW_AGGREGATE_RATING,
      reviewCount: REVIEW_COUNT,
      bestRating: 5,
      worstRating: 1,
    },
    screenshot: [
      {
        '@type': 'ImageObject',
        url: `${baseUrl}/og-image.png`,
        width: 1200,
        height: 630,
      },
    ],
    featureList: [
      'AI-powered spam detection',
      'AI-generated cold outreach detection',
      'Smart folder organization',
      'Automatic email labeling',
      'Blocklist and allowlist management',
      'Gmail integration',
      '24/7 automation',
      'Privacy-first design',
      'Multi-account support',
      'Customizable spam threshold',
    ],
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Email Ferret',
    url: baseUrl,
    description:
      'Your AI email assistant that works 24/7 to detect spam, organize your inbox, and keep you focused.',
    publisher: {
      '@type': 'Organization',
      name: 'Email Ferret',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}${getLogoPathForSEO()}`,
        width: 512,
        height: 512,
      },
    },
    // Sitelinks searchbox. The target must be a URL that actually applies the
    // query — this previously pointed at /blog?q=, which nothing read, so the
    // searchbox dropped users on an unfiltered blog index. /search reads ?q=
    // and covers every collection, not just blog posts.
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <JSONLDScript data={organizationSchema} id="organization-structured-data" />
      <JSONLDScript data={softwareApplicationSchema} id="software-application-structured-data" />
      <JSONLDScript data={webSiteSchema} id="website-structured-data" />
    </>
  );
}
